import { Box, Button } from '@mui/material';
import Lightbox from 'src/components/LightboxModal';
import * as Sentry from '@sentry/react';

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';

import ChatMessageItem from './ChatMessageItem';
import useChatClient from 'src/hooks/useChatClient';
import { useTranslation } from 'react-i18next';
import { getS3Url } from 'src/constants';

// ----------------------------------------------------------------------

export default function ChatMessageList() {
  const { t } = useTranslation('translation');
  const scrollRef = useRef(null);
  const scrollMessagesToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [scrollRef]);

  const {
    activeConversation: conversation,
    getMessages,
    subscribeToMessages,
  } = useChatClient();
  const [messagePaginator, setMessagePaginator] = useState(null);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!conversation) {
      setMessages([]);
      setMessagePaginator(null);
      return;
    }

    const conversationId = conversation.id;

    getMessages(conversationId)
      .then((result) => {
        setMessages(result.items);
        setMessagePaginator(result);
        scrollMessagesToBottom();
      })
      .catch((err) => Sentry.captureException(err));

    const unsub = subscribeToMessages(conversationId, (message) => {
      setMessages((prev) => {
        // Deduplicate by message id
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
      scrollMessagesToBottom();
    });

    return () => unsub();
  }, [
    conversation?.id,
    getMessages,
    subscribeToMessages,
    scrollMessagesToBottom,
  ]);

  const prevPage = useCallback(() => {
    if (!messagePaginator?.prevPage) {
      throw new Error('Should not be called without a message paginator');
    }

    messagePaginator
      .prevPage()
      .then((result) => {
        setMessagePaginator(result);
        setMessages((existing) => [...result.items, ...existing]);
      })
      .catch((err) => Sentry.captureException(err));
  }, [messagePaginator]);

  const hasPrevPage = useMemo(() => {
    if (!messagePaginator) return false;
    return messagePaginator.hasPrevPage || false;
  }, [messagePaginator]);

  const getMessageKey = (message) => message.id;

  const [selectedImage, setSelectedImage] = useState(-1);
  // All photo attachments in the thread, in order, for the lightbox.
  const imagesLightbox = useMemo(
    () =>
      messages.flatMap((message) =>
        (Array.isArray(message.media) ? message.media : []).map((item) => ({
          src: /^https?:\/\//.test(item.url) ? item.url : getS3Url(item.url),
        }))
      ),
    [messages]
  );
  const handleOpenLightbox = (imageUrl) => {
    const imageIndex = imagesLightbox.findIndex(
      (image) => image.src === imageUrl
    );
    setSelectedImage(imageIndex);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };

  return (
    <>
      <Box
        ref={scrollRef}
        sx={{ p: 3, pt: 6, height: 1, flexGrow: 1, overflow: 'scroll' }}
      >
        {hasPrevPage && (
          <Box display="flex" justifyContent="center">
            <Button onClick={prevPage}>{t('chat.more-messages')}</Button>
          </Box>
        )}

        {messages.map((message) => (
          <ChatMessageItem
            key={getMessageKey(message)}
            message={message}
            conversation={conversation}
            onOpenLightbox={handleOpenLightbox}
          />
        ))}
      </Box>

      <Lightbox
        photoIndex={selectedImage}
        setPhotoIndex={setSelectedImage}
        images={imagesLightbox}
        isOpen={selectedImage >= 0}
        onClose={handleCloseLightbox}
      />
    </>
  );
}
